<script setup lang="ts">
  import dayjs from 'dayjs';
  import { computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Checkbox } from '@ui/components/checkbox';
  import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
  } from '@ui/components/context-menu';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';
  import { MonetaryText } from '@ui/components/monetary';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import InvoiceListWidgetStatus from '@/components/common/invoice-list-widget/InvoiceStatus.vue';
  import PaymentPromiseSubtaskDate from '@/components/tasks/PaymentPromiseSubtaskDate.vue';
  import TaskDismissAlert from '@/components/tasks/TaskDismissAlert.vue';
  import TasksAssigneeSelect from '@/components/tasks/TasksAssigneeSelect.vue';

  interface Props {
    isDateHovered?: boolean;
    isSelected?: boolean;
    task?: Task;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    (e: 'afterSetDate', task?: Task): void;
    (e: 'afterComplete', task?: Task): void;
    (e: 'hoverDate', hovering: boolean): void;
    (e: 'openSheet', invoiceId?: string): void;
    (e: 'closeSheet'): void;
    (e: 'toggleSelect', checked: boolean): void;
  }>();
  const route = useRoute();
  const router = useRouter();
  const PaymentPromiseSubtaskDateRef = ref();

  const acknowledging = ref(false);
  const completing = ref(false);
  const showDismissAlert = ref(false);
  const dismissAlertActionType = ref<string | undefined>();

  const taskActions = computed(() => [
    {
      icon: 'fa-expand',
      label: 'View task',
      onclick: () =>
        router.push({
          query: {
            ...route.query,
            task_drawer_id: props.task?.id,
          },
        }),
      type: 'view',
    },
    ...(props.task?.isExpectsAcknowledge
      ? [
          {
            disabled: !props.task?.isOpen,
            hidden: !props.task?.isOpen,
            icon: 'fa-thumbs-up text-success',
            label: 'Acknowledge!',
            onclick: () => acknowledgeTask(),
          },
        ]
      : []),
    ...(props.task?.isExpectsComplete
      ? [
          {
            disabled: !props.task?.isOpen || !invoice.value?.promised_to_pay_date,
            hidden: !props.task?.isOpen,
            icon: 'fa-check-circle text-success',
            label: 'Mark complete',
            onclick: () => completeTask(),
          },
        ]
      : []),
    {
      disabled: !props.task?.isOpen,
      hidden: !props.task?.isOpen,
      icon: 'fa-circle-xmark text-destructive',
      label: 'Dismiss task',
      onclick: () => {
        if (!props.task || !props.task?.isOpen) return;
        openDismissAlert('close');
      },
    },
    {
      disabled: !props.task?.isOpen,
      hidden: !props.task?.isOpen,
      icon: 'fa-flag text-agent',
      label: 'Flag Inaccurate',
      onclick: () => {
        if (!props.task || !props.task?.isOpen) return;
        openDismissAlert('mark_inaccurate');
      },
    },
  ]);

  const invoice = computed(() => props.task?.invoice);
  const promiseData = computed(() => {
    const pp = props.task?.payment_promise;
    if (pp?.id) {
      return {
        invoice_id: pp.invoice_id,
        invoice_number:
          pp.invoice_id && invoice.value?.number
            ? invoice.value.number
            : pp.extracted_invoice_number,
        promised_date: pp.promised_to_pay_date,
        payment_type: pp.payment_type,
        amount: pp.amount,
        currency: pp.currency,
        is_calculated: pp.is_calculated,
        sent_date: pp.sent_date,
        outside_task_scope: pp.outside_task_scope,
        unknown_invoice_number: pp.unknown_invoice_number,
        is_promised_date_updated: pp.is_promised_date_updated,
      };
    }
    const ack = props.task?.promisesData?.[0];
    return ack ? { ...ack, currency: null as string | null } : undefined;
  });

  const completeTask = async () => {
    if (!props.task || !props.task?.isOpen) return;

    if (invoice.value?.id && !invoice.value?.promised_to_pay_date) {
      return PaymentPromiseSubtaskDateRef.value?.openDatePicker();
    }

    try {
      completing.value = true;

      const response = await stuut.tasks.complete(props.task as Task);

      toast.success('Marked as complete');
      emit('afterComplete', response);

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      completing.value = false;
    }
  };

  const acknowledgeTask = async () => {
    if (!props.task || !props.task?.isOpen) return;

    if (invoice.value?.id && !invoice.value?.promised_to_pay_date) {
      return PaymentPromiseSubtaskDateRef.value?.openDatePicker();
    }

    acknowledging.value = true;

    try {
      const response = await stuut.tasks.acknowledge(props.task as Task);

      toast.success('Acknowledged task and marked complete.');
      emit('afterComplete', response);

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      acknowledging.value = false;
    }
  };

  const closeDismissAlert = () => {
    showDismissAlert.value = false;
    dismissAlertActionType.value = undefined;
  };

  const openDismissAlert = (actionType?: string) => {
    showDismissAlert.value = true;
    dismissAlertActionType.value = actionType;
  };
</script>

<template>
  <template v-if="task?.id">
    <ContextMenu>
      <ContextMenuTrigger as-child>
        <div
          class="grid grid-cols-[36px_50px_minmax(135px,auto)_82px_70px_125px_70px_46px_82px_28px_28px_1fr] items-center gap-x-1 w-full h-10 border-t transition-colors duration-500"
          :class="isSelected ? 'bg-primary-muted/40' : ''"
        >
          <div class="flex justify-center pl-2">
            <Checkbox
              :model-value="isSelected"
              @update:model-value="(val: any) => emit('toggleSelect', !!val)"
            />
          </div>

          <div class="flex items-center justify-center gap-x-2 h-6 pr-2">
            <Tooltip :disabled="!task?.isOpen" :delay-duration="0">
              <TooltipTrigger :class="!task?.isOpen && 'cursor-default'">
                <i
                  class="far fa-check-circle transition-colors text-lg!"
                  :class="{
                    'hover:text-warning/75': !invoice?.promised_to_pay_date && task?.isOpen,
                    'hover:text-success/75': invoice?.promised_to_pay_date && task?.isOpen,
                    'fas text-success': task?.isCompleted,
                    'opacity-10': !task?.isOpen && !task?.isCompleted,
                  }"
                  @click="completeTask"
                />
              </TooltipTrigger>

              <TooltipContent side="left">
                <template v-if="invoice?.promised_to_pay_date"> Mark complete </template>

                <template v-else> Set a promised payment date and complete </template>
              </TooltipContent>
            </Tooltip>

            <Tooltip :disabled="!task?.isOpen" :delay-duration="0">
              <TooltipTrigger :class="!task?.isOpen && 'cursor-default'">
                <i
                  class="far fa-circle-xmark transition-colors text-lg!"
                  :class="{
                    'text-muted-foreground hover:text-error/75 cursor-pointer': task?.isOpen,
                    'fas text-destructive': task?.isClosed,
                    'opacity-25': !task?.isOpen && !task?.isClosed,
                  }"
                  @click="task?.isOpen && openDismissAlert('close')"
                />
              </TooltipTrigger>

              <TooltipContent side="right"> Close task </TooltipContent>
            </Tooltip>
          </div>

          <div class="flex items-center gap-x-1 pr-1">
            <Tooltip :delay-duration="0" :disabled="!!task?.invoice_id">
              <TooltipTrigger as-child>
                <Button
                  class="font-bold cursor-pointer w-fit text-foreground text-xs"
                  :class="
                    task?.invoice_id ? 'hover:underline' : 'text-destructive hover:no-underline'
                  "
                  variant="link"
                  size="inline"
                  @click="emit('openSheet', promiseData?.invoice_id)"
                >
                  {{ promiseData?.invoice_number }}
                </Button>
              </TooltipTrigger>

              <TooltipContent>Could not find a matching invoice</TooltipContent>
            </Tooltip>

            <span
              v-if="promiseData?.is_promised_date_updated"
              class="text-primary font-semibold text-3xs shrink-0"
            >
              NEW
            </span>

            <i
              v-if="!task?.invoice_id"
              class="text-destructive fas fa-exclamation-triangle text-xs shrink-0"
            />
          </div>

          <div class="flex items-center">
            <Timestamp
              v-if="invoice?.dueDateStartOfDay"
              :time="invoice.dueDateStartOfDay"
              format="MMM D YYYY"
              date-only
            />
            <span v-else class="text-muted-foreground">-</span>
          </div>

          <div class="flex items-center">
            <MonetaryText
              v-if="invoice"
              :value="invoice.amount_remaining"
              :currency="invoice.currency"
            />
            <span v-else class="text-muted-foreground">-</span>
          </div>

          <div
            class="flex items-center gap-x-2"
            @mouseenter="isSelected && emit('hoverDate', true)"
            @mouseleave="isSelected && emit('hoverDate', false)"
          >
            <template v-if="task?.invoice_id">
              <i
                v-if="task?.isExpectsComplete && !invoice?.promised_to_pay_date"
                class="text-destructive far fa-calendar-circle-exclamation text-sm"
              />

              <PaymentPromiseSubtaskDate
                ref="PaymentPromiseSubtaskDateRef"
                :task="task"
                :invoice="invoice ?? undefined"
                :selecting-multiple="isSelected"
                :is-date-hovered="isSelected && isDateHovered"
                :promise-data="promiseData"
                @after-set-date="emit('afterSetDate', task)"
              />
            </template>

            <template v-else>
              <Timestamp
                v-if="promiseData?.promised_date"
                :time="dayjs(promiseData.promised_date)"
                format="MMM D YYYY"
                date-only
                class="text-destructive font-semibold"
              />
            </template>
          </div>

          <div class="flex items-center">
            <MonetaryText
              v-if="promiseData?.amount"
              :value="promiseData.amount"
              :currency="promiseData.currency || invoice?.currency"
              major-unit
            />
            <span v-else class="text-muted-foreground">-</span>
          </div>

          <span class="capitalize">
            {{ promiseData?.payment_type || '-' }}
          </span>

          <div class="flex items-center">
            <InvoiceListWidgetStatus v-if="invoice" :invoice="invoice" />
          </div>

          <div class="flex items-center justify-center">
            <TasksAssigneeSelect size="sm" :task="task" :show-avatar-only="true" />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant="ghost" size="icon" class="size-6" :disabled="!task?.isOpen">
                <i class="fa-solid fa-ellipsis-vertical"></i>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
              <DropdownMenuGroup>
                <template v-for="action in taskActions" :key="action.label">
                  <DropdownMenuItem
                    v-if="!action.hidden"
                    class="flex items-center cursor-pointer gap-4"
                    :disabled="action.disabled"
                    @click="action.onclick()"
                  >
                    <i class="fa-solid" :class="action.icon"></i>
                    <span>{{ action.label }}</span>
                  </DropdownMenuItem>
                </template>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <TaskDismissAlert
            v-if="task"
            :task="task"
            :show="showDismissAlert"
            :action-type="dismissAlertActionType"
            @close="closeDismissAlert"
          />
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent>
        <template v-for="action in taskActions" :key="action.label">
          <ContextMenuItem
            v-if="!action.hidden"
            class="flex items-center cursor-pointer gap-4"
            :disabled="action.disabled"
            @click="action.onclick()"
          >
            <i class="fa-solid" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </ContextMenuItem>
        </template>
      </ContextMenuContent>
    </ContextMenu>
  </template>
</template>

<style scoped></style>
